import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Collapse, Paper, Skeleton, Stack, Tab, Tabs, Typography, alpha } from '@mui/material';
import {
  ScheduleOutlined, MenuBookOutlined, EventBusyOutlined, MeetingRoomOutlined, ArrowForwardRounded,
  CheckCircleRounded, ErrorOutline, WarningAmberRounded, ExpandMore, ExpandLess,
} from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useSessionContext } from '../../context/SessionContext';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { timetableService, LessonsOverview, ProblemFix } from '../../services/timetableService';
import EmptyState from '../../components/common/EmptyState';
import ProblemList from '../../components/timetable/ProblemList';
import SchoolDayTab from '../../components/timetable/setup/SchoolDayTab';
import LessonsTab from '../../components/timetable/setup/LessonsTab';
import AvailabilityTab from '../../components/timetable/setup/AvailabilityTab';
import RoomsTab from '../../components/timetable/setup/RoomsTab';

type TabKey = 'day' | 'lessons' | 'availability' | 'rooms';

const TABS: { key: TabKey; label: string; icon: React.ReactElement; fix: ProblemFix; permission: string }[] = [
  { key: 'day', label: 'School day', icon: <ScheduleOutlined fontSize="small" />, fix: 'school_day', permission: 'timetable_config' },
  { key: 'lessons', label: 'Lessons', icon: <MenuBookOutlined fontSize="small" />, fix: 'lessons', permission: 'timetable_config' },
  { key: 'availability', label: 'Availability', icon: <EventBusyOutlined fontSize="small" />, fix: 'availability', permission: 'timetable_config' },
  { key: 'rooms', label: 'Rooms', icon: <MeetingRoomOutlined fontSize="small" />, fix: 'rooms', permission: 'rooms' },
];

/** Everything a timetable is built from: the school day, lessons, availability and rooms. */
const TimetableSetupPage: React.FC = () => {
  const { viewSession, activeSession } = useSessionContext();
  const session = viewSession ?? activeSession;
  const sessionId = session?._id ?? '';
  const { canRead, canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const [params, setParams] = useSearchParams();
  const [overview, setOverview] = useState<LessonsOverview | null>(null);
  const [showProblems, setShowProblems] = useState(false);
  const request = useRef(0);

  const tabs = useMemo(() => TABS.filter((t) => canRead(t.permission)), [canRead]);
  const tab = tabs.find((t) => t.key === params.get('tab')) ?? tabs[0];
  const configReadable = canRead('timetable_config');
  const [visited, setVisited] = useState<Set<TabKey>>(() => new Set());
  useEffect(() => {
    if (tab) setVisited((v) => (v.has(tab.key) ? v : new Set(v).add(tab.key)));
  }, [tab]);
  const opened = (key: TabKey) => tab?.key === key || visited.has(key);

  const load = useCallback(async () => {
    if (!sessionId || !configReadable) return;
    const id = ++request.current;
    try {
      const data = await timetableService.getLessons(sessionId);
      if (id === request.current) setOverview(data);
    } catch (err: any) {
      if (id === request.current) enqueueSnackbar(err?.message || 'Could not load the timetable setup', { variant: 'error' });
    }
  }, [sessionId, configReadable, enqueueSnackbar]);

  useEffect(() => { setOverview(null); load(); }, [load]);

  const changeTab = (key: TabKey) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set('tab', key);
    return next;
  }, { replace: true });

  if (!session) {
    return <EmptyState icon={<ScheduleOutlined />} title="No active session" description="Start an academic session to set up its timetable." />;
  }
  if (!tab) {
    return <EmptyState icon={<ScheduleOutlined />} title="No access" description="You don't have permission to set up the timetable." />;
  }

  const problems = overview?.problems ?? [];
  const errors = problems.filter((p) => p.severity === 'error').length;
  const warnings = problems.length - errors;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} color="text.primary">Timetable setup</Typography>
        <Typography variant="body2" color="text.secondary">
          {session.name}: the school day, lessons, availability and rooms every timetable is built from.
        </Typography>
      </Box>

      {configReadable && (
        <Paper
          variant="outlined"
          sx={(theme) => {
            const tone = !overview ? theme.palette.divider : errors ? theme.palette.error.main : warnings ? theme.palette.warning.main : theme.palette.success.main;
            return { mb: 2, borderRadius: 2.5, overflow: 'hidden', borderColor: overview ? alpha(tone, 0.35) : 'divider' };
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.25 }}>
            {!overview ? (
              <Skeleton width={260} />
            ) : (
              <>
                {errors
                  ? <ErrorOutline color="error" fontSize="small" />
                  : warnings ? <WarningAmberRounded color="warning" fontSize="small" /> : <CheckCircleRounded color="success" fontSize="small" />}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {errors
                      ? `${errors} thing${errors === 1 ? '' : 's'} to fix before the timetable can be generated`
                      : warnings ? 'Ready to generate, with a few things worth a look' : 'Ready to generate'}
                  </Typography>
                  {!errors && (
                    <Typography variant="caption" color="text.secondary">
                      {overview.lessons.length} lessons across {new Set(overview.lessons.map((l) => l.sectionKey)).size} classes
                    </Typography>
                  )}
                </Box>
                {problems.length > 0 && (
                  <Button size="small" color="inherit" onClick={() => setShowProblems((v) => !v)} endIcon={showProblems ? <ExpandLess /> : <ExpandMore />} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                    {showProblems ? 'Hide' : `Show ${problems.length}`}
                  </Button>
                )}
                {!errors && canRead('timetable') && (
                  <Button component={RouterLink} to="/app/timetable/board" size="small" variant="contained" endIcon={<ArrowForwardRounded />} sx={{ textTransform: 'none' }}>
                    Open timetable
                  </Button>
                )}
              </>
            )}
          </Stack>
          <Collapse in={showProblems && problems.length > 0} unmountOnExit>
            <Box sx={{ px: 2, pb: 2 }}>
              <ProblemList problems={problems} sessionId={sessionId} hereFix={tab.fix} />
            </Box>
          </Collapse>
        </Paper>
      )}

      <Paper elevation={0} sx={{ mb: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={tab.key}
          onChange={(_, v: TabKey) => changeTab(v)}
          variant="scrollable"
          sx={{ px: 1, minHeight: 44, '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, minHeight: 44, gap: 0.75 } }}
        >
          {tabs.map((t) => <Tab key={t.key} value={t.key} icon={t.icon} iconPosition="start" label={t.label} />)}
        </Tabs>
      </Paper>

      {/* A tab stays mounted once opened, so switching back is instant and keeps its state. */}
      {opened('day') && (
        <Box hidden={tab.key !== 'day'}>
          <SchoolDayTab sessionId={sessionId} canWrite={canWrite('timetable_config')} onSaved={load} />
        </Box>
      )}
      {opened('rooms') && (
        <Box hidden={tab.key !== 'rooms'}>
          <RoomsTab canWrite={canWrite('rooms')} />
        </Box>
      )}
      {(tab.key === 'lessons' || tab.key === 'availability') && !overview && (
        <Stack spacing={2}>
          {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={180} />)}
        </Stack>
      )}
      {overview && opened('lessons') && (
        <Box hidden={tab.key !== 'lessons'}>
          <LessonsTab sessionId={sessionId} overview={overview} canWrite={canWrite('timetable_config')} onChanged={load} />
        </Box>
      )}
      {overview && opened('availability') && (
        <Box hidden={tab.key !== 'availability'}>
          <AvailabilityTab sessionId={sessionId} overview={overview} canWrite={canWrite('timetable_config')} onChanged={load} />
        </Box>
      )}
    </Box>
  );
};

export default TimetableSetupPage;
